import { useState, useEffect } from 'react'
import {
  FiClock, FiEdit, FiCalendar, FiAlertCircle, FiChevronDown, FiChevronUp,
  FiList, FiGrid, FiCheck, FiXCircle, FiLoader, FiCheckSquare, FiSquare, FiCoffee, FiTrendingUp
} from 'react-icons/fi'
import * as FiIcons from 'react-icons/fi'
import toast from 'react-hot-toast'
import CorreccionDiaModal from '../../components/v2/CorreccionDiaModal'
import CorreccionesDiaPanel from '../../components/v2/CorreccionesDiaPanel'
import SolicitudAusenciaModal from '../../components/v2/SolicitudAusenciaModal'
import { API_URL } from '../../config/api'
import { tiposPausaAPI, controlFichajesAPI, festivosAPI, horasExtrasAPI } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { canAccess, getEffectivePlan } from '../../config/planFeatures'
import ModalHoraExtra from '../../components/v2/ModalHoraExtra'
import { PageHeader, Stat, Card, Btn, Empty, Pill } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * HistorialFichajes (V2) — versión rediseñada con DS v2.
 * Misma lógica funcional que el original; cambian:
 *   - PageHeader v2 con eyebrow numerada y CTA
 *   - 3 Stat cards (días, horas, media)
 *   - FilterSelect en pill v2
 *   - ui-card en lugar de card-glass
 *   - Pill primitive en lugar de badge-*
 *   - Tokens var(--*) en lugar de gray-*
 */

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

function FilterSelect({ icon: Icon, label, value, onChange, children }) {
  return (
    <div className="inline-flex items-center gap-1.5 h-9 px-3 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[12.5px]">
      {Icon && <Icon size={13} className="text-[var(--ink-3)] flex-shrink-0" />}
      <label className="text-[var(--ink-3)] whitespace-nowrap">{label}</label>
      <select value={value} onChange={onChange} className="bg-transparent outline-none text-[var(--ink)] font-medium cursor-pointer">
        {children}
      </select>
    </div>
  )
}

// Mapeo de estado de fichaje → Pill tone
const estadoPillProps = (fichaje) => {
  if (fichaje.hora_salida) return { tone: 'emerald', label: 'Completo' }
  if (fichaje.estado === 'pausado') return { tone: 'amber', label: 'Pausado' }
  return { tone: 'brand', label: 'En curso' }
}

const HistorialFichajesV2 = () => {
  const { empresaInfo } = useAuth()
  // El flujo de correcciones es del plan Avanzado. Sin esta comprobación los
  // botones se mostraban igual en Pro y al pulsarlos solo salía un error: se
  // ofrecía algo que el usuario no puede usar.
  const planActual = getEffectivePlan(empresaInfo)
  const puedeCorregir = canAccess(planActual, 'correcciones_flujo')
  const puedeAusencias = canAccess(planActual, 'ausencias')

  const [fichajes, setFichajes] = useState([])
  const [loading, setLoading] = useState(true)
  const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1)
  const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear())
  const [resumenMensual, setResumenMensual] = useState(null)
  // Punto C: una sola entrada de corrección a nivel de marcaje (sustituye a los
  // antiguos modales de fichaje/pausa/pausa-olvidada). Solo lleva la fecha.
  const [modalCorreccion, setModalCorreccion] = useState({ open: false, fecha: null })
  const [fichajeExpandido, setFichajeExpandido] = useState(null)
  const [vistaCalendario, setVistaCalendario] = useState(false)
  const [tiposPausa, setTiposPausa] = useState([])
  const [diasExpandidos, setDiasExpandidos] = useState([])
  const [misSolicitudes, setMisSolicitudes] = useState([])
  const [diasSinFichaje, setDiasSinFichaje] = useState([])
  const [modalAusencia, setModalAusencia] = useState({ open: false, fecha: null, fechaFin: null })
  const [misHorasExtras, setMisHorasExtras] = useState([])
  const [modalHoraExtra, setModalHoraExtra] = useState(null)
  const [diasOlvidadosSeleccionados, setDiasOlvidadosSeleccionados] = useState([])
  const [misAusencias, setMisAusencias] = useState([])
  const [festivos, setFestivos] = useState([])
  const [propuestas, setPropuestas] = useState([])
  const [rechazoId, setRechazoId] = useState(null)
  const [comentarioRechazo, setComentarioRechazo] = useState('')
  const [loadingPropuesta, setLoadingPropuesta] = useState(null)

  useEffect(() => {
    cargarTiposPausa()
    cargarFichajes()
    // Las llamadas a /correcciones y /ausencias devuelven 403 sin el plan
    // Avanzado: no se lanzan si la feature no está disponible.
    if (puedeCorregir) {
      cargarPropuestas()
      cargarMisSolicitudes()
    }
    cargarDiasSinFichaje()
    if (puedeAusencias) cargarMisAusencias()
    cargarFestivos()
    cargarMisHorasExtras()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSeleccionado, anioSeleccionado])

  useEffect(() => {
    let fechaActual = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
    const checkDayChange = setInterval(() => {
      const nuevaFecha = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      if (nuevaFecha !== fechaActual) {
        fechaActual = nuevaFecha
        const ahora = new Date()
        const mesActual = (ahora.getMonth() + 1).toString().padStart(2, '0')
        const anioActual = ahora.getFullYear().toString()
        if (mesSeleccionado === mesActual && anioSeleccionado === anioActual) cargarFichajes()
      }
    }, 60000)
    return () => clearInterval(checkDayChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mesSeleccionado, anioSeleccionado])

  const cargarTiposPausa = async () => {
    try {
      const response = await tiposPausaAPI.getAll()
      setTiposPausa(response.data || [])
    } catch { toast.error('Error al cargar tipos de pausa') }
  }

  const cargarPropuestas = async () => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/correcciones/mis-propuestas?estado=pendiente`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) setPropuestas(await r.json())
    } catch { /* silencioso */ }
  }

  const cargarMisSolicitudes = async () => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/correcciones/mis-solicitudes`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) setMisSolicitudes(await r.json())
    } catch { /* silencioso */ }
  }

  const cargarMisHorasExtras = async () => {
    try {
      const res = await horasExtrasAPI.misSolicitudes({ mes: mesSeleccionado, anio: anioSeleccionado })
      setMisHorasExtras(res.data || [])
    } catch { /* silencioso */ }
  }

  const abrirModalHoraExtra = async (fichaje) => {
    try {
      const res = await horasExtrasAPI.check(fichaje.id)
      const d = res.data
      if (d.ya_solicitada) { toast('Ya existe una solicitud de hora extra para este fichaje', { icon: 'ℹ️' }); return }
      if (d.sin_calendario) { toast.error('No hay calendario asignado. Configura un calendario para calcular las horas extra.'); return }
      if (d.minutos_extra <= 0) { toast('No hay horas extra en este fichaje', { icon: 'ℹ️' }); return }
      setModalHoraExtra({ fichaje_id: fichaje.id, minutosExtra: d.minutos_extra, minutosTurno: d.minutos_turno ?? null })
    } catch { toast.error('No se pudo calcular las horas extra') }
  }

  const cargarDiasSinFichaje = async () => {
    try {
      const mes = String(mesSeleccionado).padStart(2, '0')
      const lastDay = new Date(anioSeleccionado, mesSeleccionado, 0).getDate()
      const fecha_inicio = `${anioSeleccionado}-${mes}-01`
      const fecha_fin = `${anioSeleccionado}-${mes}-${String(lastDay).padStart(2, '0')}`
      const response = await controlFichajesAPI.getDiasSinFichaje({ estado: 'pendiente', fecha_inicio, fecha_fin })
      setDiasSinFichaje(response.data || [])
    } catch { /* silencioso */ }
  }

  const cargarMisAusencias = async () => {
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/ausencias/mis-ausencias?anio=${anioSeleccionado}&estado=aprobada`, { headers: { 'Authorization': `Bearer ${token}` } })
      if (r.ok) {
        const data = await r.json()
        const mes = String(mesSeleccionado).padStart(2, '0')
        const mesInicio = `${anioSeleccionado}-${mes}-01`
        const lastDay = new Date(anioSeleccionado, mesSeleccionado, 0).getDate()
        const mesFin = `${anioSeleccionado}-${mes}-${String(lastDay).padStart(2, '0')}`
        setMisAusencias(data.filter(a => a.fecha_inicio <= mesFin && a.fecha_fin >= mesInicio))
      }
    } catch { /* silencioso */ }
  }

  const getAusenciaParaFecha = (fecha) => misAusencias.find(a => fecha >= a.fecha_inicio && fecha <= a.fecha_fin)

  const cargarFestivos = async () => {
    try {
      const response = await festivosAPI.getAll({ anio: String(anioSeleccionado) })
      setFestivos(response.data || [])
    } catch { /* silencioso */ }
  }

  const getFestivoParaFecha = (fecha) => festivos.find(f => f.fecha === fecha)
  const getCorreccionFichaje = (fichajeId) => misSolicitudes.find(s => s.fichaje_id === fichajeId && !s.pausa_id && s.estado !== 'aprobada')
  const getCorreccionPausa = (pausaId) => misSolicitudes.find(s => s.pausa_id === pausaId && s.estado !== 'aprobada')

  const handleResponderPropuesta = async (id, accion, comentario = '') => {
    try {
      setLoadingPropuesta(id)
      const token = localStorage.getItem('token')
      const r = await fetch(`${API_URL}/correcciones/${id}/responder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ accion, comentario })
      })
      if (r.ok) {
        toast.success(accion === 'aprobar' ? 'Corrección aceptada' : 'Corrección rechazada')
        cargarPropuestas(); cargarFichajes(); cargarMisSolicitudes()
      } else {
        const error = await r.json()
        toast.error(error.error || 'Error al procesar')
      }
    } catch { toast.error('Error al procesar propuesta') }
    finally { setLoadingPropuesta(null); setRechazoId(null); setComentarioRechazo('') }
  }

  const handleRechazarPropuesta = (id) => {
    if (rechazoId === id) {
      if (!comentarioRechazo.trim()) { toast.error('Indica el motivo del rechazo'); return }
      handleResponderPropuesta(id, 'rechazar', comentarioRechazo)
    } else {
      setRechazoId(id)
      setComentarioRechazo('')
    }
  }

  const cargarFichajes = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const r = await fetch(
        `${API_URL}/fichajes/historial?mes=${mesSeleccionado}&anio=${anioSeleccionado}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      )
      if (r.ok) {
        const data = await r.json()
        setFichajes(data.fichajes)
        calcularResumenDesdeFichajes(data.fichajes)
      }
    } catch { toast.error('Error al cargar historial') }
    finally { setLoading(false) }
  }

  const calcularResumenDesdeFichajes = (fichajesData) => {
    const fichajesCompletados = fichajesData.filter(f => f.hora_salida && f.tiempo_total)
    const diasUnicos = new Set(fichajesCompletados.map(f => f.fecha)).size
    const diasTrabajados = diasUnicos
    const totalMinutos = fichajesCompletados.reduce((sum, f) => sum + (f.tiempo_total || 0), 0)
    const horasTotales = Math.floor(totalMinutos / 60)
    const minutosRestantes = totalMinutos % 60
    let mediaDiaria = '0:00'
    if (diasTrabajados > 0) {
      const promedioMinutos = totalMinutos / diasTrabajados
      const h = Math.floor(promedioMinutos / 60)
      const m = Math.round(promedioMinutos % 60)
      mediaDiaria = `${h}:${String(m).padStart(2, '0')}`
    }
    setResumenMensual({
      dias_trabajados: diasTrabajados,
      horas_totales: `${horasTotales}:${String(minutosRestantes).padStart(2, '0')}`,
      horas_promedio: mediaDiaria
    })
  }

  const formatearDuracion = (minutos) => {
    if (!minutos && minutos !== 0) return '-'
    const horas = Math.floor(minutos / 60)
    const mins = minutos % 60
    return `${horas}h ${mins}m`
  }


  const toggleExpandir = (id) => setFichajeExpandido(fichajeExpandido === id ? null : id)
  const toggleDiaExpandido = (fecha) =>
    setDiasExpandidos(prev => prev.includes(fecha) ? prev.filter(f => f !== fecha) : [...prev, fecha])

  const agruparFichajesPorDia = () => {
    const grupos = {}
    fichajes.forEach(fichaje => {
      if (!grupos[fichaje.fecha]) grupos[fichaje.fecha] = []
      grupos[fichaje.fecha].push(fichaje)
    })
    return grupos
  }

  const fichajesPorDia = agruparFichajesPorDia()

  const todasLasFechas = () => {
    const fechas = new Set()
    Object.keys(fichajesPorDia).forEach(f => fechas.add(f))
    diasSinFichaje.forEach(d => fechas.add(d.fecha))
    const mes = String(mesSeleccionado).padStart(2, '0')
    const mesInicio = `${anioSeleccionado}-${mes}-01`
    const lastDay = new Date(anioSeleccionado, mesSeleccionado, 0).getDate()
    const mesFin = `${anioSeleccionado}-${mes}-${String(lastDay).padStart(2, '0')}`
    misAusencias.forEach(a => {
      const inicio = a.fecha_inicio > mesInicio ? a.fecha_inicio : mesInicio
      const fin = a.fecha_fin < mesFin ? a.fecha_fin : mesFin
      for (let d = new Date(inicio + 'T00:00:00'); d <= new Date(fin + 'T00:00:00'); d.setDate(d.getDate() + 1)) {
        const dayOfWeek = d.getDay()
        if (dayOfWeek !== 0 && dayOfWeek !== 6) fechas.add(d.toISOString().split('T')[0])
      }
    })
    return Array.from(fechas).sort((a, b) => b.localeCompare(a))
  }

  const getTipoPausaInfo = (tipoCodigo) => {
    const tipo = tiposPausa.find(t => t.codigo === tipoCodigo)
    if (!tipo) return { codigo: tipoCodigo, nombre: tipoCodigo, icono: FiIcons.FiClock }
    const IconComponent = FiIcons[tipo.icono] || FiIcons.FiClock
    return { ...tipo, icono: IconComponent }
  }

  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i)

  const generarCalendario = () => {
    const year = anioSeleccionado
    const month = mesSeleccionado - 1
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    let startDay = firstDay.getDay() || 7
    startDay--
    const fichajesPorFecha = {}
    fichajes.forEach(f => { fichajesPorFecha[f.fecha] = f })
    const dias = []
    for (let i = 0; i < startDay; i++) dias.push(null)
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const fecha = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
      dias.push({ dia: d, fecha, fichaje: fichajesPorFecha[fecha] || null })
    }
    return dias
  }

  const calendarioDias = generarCalendario()

  // ── RENDER FICHAJE ROW (vista desktop) ──
  const renderFichajeRow = (fichaje) => {
    const tienePausas = fichaje.pausas && fichaje.pausas.length > 0
    const estaExpandido = fichajeExpandido === fichaje.id
    const estPill = estadoPillProps(fichaje)
    const corr = getCorreccionFichaje(fichaje.id)
    const heExistente = fichaje.hora_salida ? misHorasExtras.find(h => h.fichaje_id === fichaje.id) : null

    return (
      <>
        <div className="px-4 py-3 grid grid-cols-6 gap-3 items-center text-[13px]">
          <div>
            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Entrada</div>
            <div className="flex items-center gap-1 font-mono tnum text-[var(--ink)]">
              <FiClock size={11} className="text-[#0F8C66]" />
              {fichaje.hora_entrada}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Salida</div>
            {fichaje.hora_salida ? (
              <div className="flex items-center gap-1 font-mono tnum text-[var(--ink)]">
                <FiClock size={11} className="text-[#C13030]" />
                {fichaje.hora_salida}
                {fichaje.cross_day && <span className="text-[10px] text-[#A86A06] font-medium">(+1D)</span>}
              </div>
            ) : (
              <span className="text-[var(--ink-3)]">—</span>
            )}
          </div>
          <div>
            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Pausas</div>
            {tienePausas ? (
              <button
                type="button"
                onClick={() => toggleExpandir(fichaje.id)}
                className="inline-flex items-center gap-1 text-[var(--brand-1)] hover:underline"
              >
                <Pill tone="amber">{fichaje.pausas.length}</Pill>
                {estaExpandido ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
              </button>
            ) : fichaje.hora_salida ? (
              <button
                type="button"
                onClick={() => toggleExpandir(fichaje.id)}
                className="inline-flex items-center gap-1 text-[#A86A06] hover:underline"
              >
                <FiCoffee size={12} />
                {estaExpandido ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
              </button>
            ) : (
              <span className="text-[var(--ink-3)]">—</span>
            )}
          </div>
          <div>
            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Tiempo</div>
            <div className="font-mono tnum font-medium text-[var(--ink)]">
              {formatearDuracion(fichaje.tiempo_efectivo || fichaje.tiempo_total)}
            </div>
          </div>
          <div>
            <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-1">Estado</div>
            <div className="flex items-center gap-1 flex-wrap">
              <Pill tone={estPill.tone}>{estPill.label}</Pill>
              {fichaje.tiene_correccion === 1 && (
                <Pill tone="emerald" className="!px-1.5" title="Fichaje corregido">
                  <FiCheck size={10} />
                </Pill>
              )}
              {corr?.estado === 'pendiente' && (
                <Pill
                  tone="amber"
                  className="!px-1.5"
                  title={`Solicitud de corrección pendiente: ${corr.hora_entrada_corregida || corr.hora_salida_corregida} (esperando aprobación del administrador)`}
                >
                  <FiLoader size={10} />
                </Pill>
              )}
              {corr?.estado === 'rechazada' && (
                <Pill tone="red" className="!px-1.5" title={corr.comentario_admin || 'Rechazada'}><FiXCircle size={10} /></Pill>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-1.5">
            {puedeCorregir && (
              <button
                type="button"
                onClick={() => setModalCorreccion({ open: true, fecha: fichaje.fecha })}
                className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[var(--brand-1)] hover:bg-[rgba(37,99,235,.06)] transition-colors"
                title="Solicitar corrección"
              >
                <FiEdit size={14} />
              </button>
            )}
            {fichaje.hora_salida && heExistente && (
              <Pill tone={heExistente.estado === 'aprobada' ? 'emerald' : heExistente.estado === 'rechazada' ? 'red' : 'amber'}>
                HE {heExistente.estado}
              </Pill>
            )}
            {fichaje.hora_salida && !heExistente && (
              <button
                type="button"
                onClick={() => abrirModalHoraExtra(fichaje)}
                className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[#7C3AED] hover:bg-[rgba(124,58,237,.06)] transition-colors"
                title="Solicitar hora extra"
              >
                <FiTrendingUp size={14} />
              </button>
            )}
          </div>
        </div>

        {estaExpandido && (
          <div className="px-4 pb-3 bg-[var(--bg-2)]/40 border-t border-[var(--line)]">
            {tienePausas && <div className="text-[10px] text-[var(--ink-3)] font-mono uppercase tracking-wider mb-2 mt-2">Detalle de pausas</div>}
            <div className="flex flex-wrap gap-2">
              {fichaje.pausas?.map((pausa, idx) => {
                const tipoPausa = getTipoPausaInfo(pausa.tipo)
                const Icono = tipoPausa.icono
                const corrP = getCorreccionPausa(pausa.id)
                return (
                  <div
                    key={pausa.id || idx}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] text-[11.5px]"
                  >
                    <Icono size={11} className="text-[var(--ink-3)]" />
                    <span className="font-medium text-[var(--ink-2)]">{tipoPausa.nombre}:</span>
                    <span className="font-mono tnum">{pausa.hora_inicio} – {pausa.hora_fin || 'En curso'}</span>
                    {pausa.duracion_minutos > 0 && (
                      <span className="font-mono tnum text-[var(--ink-3)]">({formatearDuracion(pausa.duracion_minutos)})</span>
                    )}
                    {pausa.hora_fin && puedeCorregir && (
                      <button
                        type="button"
                        onClick={() => setModalCorreccion({ open: true, fecha: fichaje.fecha })}
                        className="ml-1 p-0.5 text-[var(--ink-3)] hover:text-[var(--brand-1)] rounded transition-colors"
                        title="Solicitar corrección de pausa"
                      >
                        <FiEdit size={11} />
                      </button>
                    )}
                    {corrP?.estado === 'pendiente' && (
                      <Pill tone="amber"><FiLoader size={9} /> Pendiente</Pill>
                    )}
                    {corrP?.estado === 'rechazada' && (
                      <Pill tone="red" title={corrP.comentario_admin || 'Rechazada'}><FiXCircle size={9} /> Rechazada</Pill>
                    )}
                  </div>
                )
              })}
            </div>
            {fichaje.hora_salida && puedeCorregir && (
              <button
                type="button"
                onClick={() => setModalCorreccion({ open: true, fecha: fichaje.fecha })}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-pill bg-[rgba(245,158,11,.08)] text-[#A86A06] border border-[rgba(245,158,11,.2)] hover:bg-[rgba(245,158,11,.12)] transition-colors"
              >
                <FiCoffee size={13} /> Añadir pausa olvidada
              </button>
            )}
          </div>
        )}
      </>
    )
  }

  return (
    <>
      <PageHeader
        num="§ H1"
        kicker="Mi calendario"
        title="Mi historial."
        italic="Jornada a jornada."
        sub={`${MESES[mesSeleccionado - 1]} ${anioSeleccionado} · ${fichajes.length} ${fichajes.length === 1 ? 'fichaje' : 'fichajes'}`}
        actions={
          <div className="flex items-center gap-1 rounded-pill bg-[var(--bg-2)] border border-[var(--line)] p-1">
            <button
              type="button"
              onClick={() => setVistaCalendario(false)}
              className={`inline-flex items-center gap-1.5 px-3 h-7 text-[12px] rounded-pill transition-colors ${
                !vistaCalendario ? 'bg-[var(--bg)] text-[var(--ink)] shadow-sm' : 'text-[var(--ink-3)] hover:text-[var(--ink)]'
              }`}
            >
              <FiList size={13} /> Lista
            </button>
            <button
              type="button"
              onClick={() => setVistaCalendario(true)}
              className={`inline-flex items-center gap-1.5 px-3 h-7 text-[12px] rounded-pill transition-colors ${
                vistaCalendario ? 'bg-[var(--bg)] text-[var(--ink)] shadow-sm' : 'text-[var(--ink-3)] hover:text-[var(--ink)]'
              }`}
            >
              <FiGrid size={13} /> Calendario
            </button>
          </div>
        }
      />

      <div className="px-7 py-6 space-y-6">
        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect
            icon={FiCalendar}
            label="Mes"
            value={mesSeleccionado}
            onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
          >
            {MESES.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </FilterSelect>
          <FilterSelect
            label="Año"
            value={anioSeleccionado}
            onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
          >
            {anios.map(a => <option key={a} value={a}>{a}</option>)}
          </FilterSelect>
        </div>

        {/* KPIs */}
        {resumenMensual && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat
              label="Días trabajados"
              value={resumenMensual.dias_trabajados || 0}
              sub={`${MESES[mesSeleccionado - 1]} ${anioSeleccionado}`}
            />
            <Stat
              label="Horas totales"
              value={resumenMensual.horas_totales || '0:00'}
              unit="h"
              sub="Suma de tiempo total"
            />
            <Stat
              label="Media diaria"
              value={resumenMensual.horas_promedio || '0:00'}
              unit="h"
              sub="Total ÷ días trabajados"
            />
          </div>
        )}

        {/* Punto C — correcciones a nivel de marcaje (modelo nuevo) */}
        {puedeCorregir && (
          <CorreccionesDiaPanel onCambio={() => { cargarFichajes(); cargarDiasSinFichaje() }} />
        )}

        {/* Propuestas pendientes (modelo legacy, solo si quedan datos antiguos) */}
        {propuestas.length > 0 && (
          <Card
            eyebrow="Atención"
            title={`Correcciones propuestas por administrador (${propuestas.length})`}
            bodyClassName="p-0"
          >
            <div className="divide-y divide-[var(--line)]">
              {propuestas.map(propuesta => (
                <div key={propuesta.id} className="px-5 py-4 bg-[rgba(245,158,11,.04)]">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-[13px] font-medium text-[var(--ink)] mb-1">
                        {formatearFecha(propuesta.fecha)}
                      </div>
                      {propuesta.solicitante_nombre && (
                        <div className="text-[11px] text-[var(--ink-3)] mb-2">Propuesto por: {propuesta.solicitante_nombre}</div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-[12.5px] mb-2">
                        <div>
                          <span className="text-[var(--ink-3)]">Original: </span>
                          <span className="font-mono tnum">{propuesta.hora_entrada_original || '-'} – {propuesta.hora_salida_original || '-'}</span>
                        </div>
                        <div>
                          <span className="text-[var(--ink-3)]">Propuesto: </span>
                          <span className="font-mono tnum font-medium text-[#0F8C66]">
                            {propuesta.hora_entrada_corregida} – {propuesta.hora_salida_corregida || '-'}
                          </span>
                        </div>
                      </div>
                      <div className="text-[12.5px] text-[var(--ink-2)]">
                        <strong>Motivo:</strong> {propuesta.motivo}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      {rechazoId === propuesta.id && (
                        <textarea
                          value={comentarioRechazo}
                          onChange={(e) => setComentarioRechazo(e.target.value)}
                          placeholder="Motivo del rechazo…"
                          className="w-full sm:w-48 px-2 py-1.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] focus:outline-none focus:border-[var(--ink-3)] resize-none"
                          rows={2}
                          autoFocus
                        />
                      )}
                      <div className="flex gap-2">
                        <Btn
                          variant="primary"
                          size="sm"
                          onClick={() => handleResponderPropuesta(propuesta.id, 'aprobar')}
                          disabled={loadingPropuesta === propuesta.id}
                        >
                          <FiCheck size={12} /> Aceptar
                        </Btn>
                        <Btn
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRechazarPropuesta(propuesta.id)}
                          disabled={loadingPropuesta === propuesta.id}
                        >
                          <FiXCircle size={12} />
                          {rechazoId === propuesta.id ? 'Confirmar' : 'Rechazar'}
                        </Btn>
                        {rechazoId === propuesta.id && (
                          <Btn variant="soft" size="sm" onClick={() => { setRechazoId(null); setComentarioRechazo('') }}>
                            Cancelar
                          </Btn>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Calendario */}
        {vistaCalendario && (
          <Card eyebrow="Vista calendario" title={`${MESES[mesSeleccionado - 1]} ${anioSeleccionado}`} bodyClassName="p-0">
            <div className="grid grid-cols-7 gap-px bg-[var(--line)]">
              {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(d => (
                <div key={d} className="bg-[var(--bg-2)] text-center text-[10px] font-mono uppercase tracking-wider text-[var(--ink-3)] py-2">
                  {d}
                </div>
              ))}
              {calendarioDias.map((item, i) => {
                if (!item) return <div key={`empty-${i}`} className="bg-[var(--bg)] min-h-[70px]" />
                const f = item.fichaje
                const esDiaSinFichaje = diasSinFichaje.some(d => d.fecha === item.fecha)
                const ausenciaDelDia = !f ? getAusenciaParaFecha(item.fecha) : null
                const festivoDelDia = getFestivoParaFecha(item.fecha)
                let bgColor = 'bg-[var(--bg)]'
                let textColor = 'text-[var(--ink-3)]'
                if (f) {
                  if (f.hora_salida) {
                    bgColor = f.tiene_correccion ? 'bg-[rgba(37,99,235,.06)]' : 'bg-[rgba(16,185,129,.06)]'
                    textColor = f.tiene_correccion ? 'text-[#2563EB]' : 'text-[#0F8C66]'
                  } else {
                    bgColor = 'bg-[rgba(239,68,68,.06)]'
                    textColor = 'text-[#C13030]'
                  }
                } else if (festivoDelDia) {
                  bgColor = 'bg-[rgba(244,63,94,.06)]'
                  textColor = 'text-[#E11D48]'
                } else if (ausenciaDelDia) {
                  bgColor = 'bg-[rgba(124,58,237,.06)]'
                  textColor = 'text-[#7C3AED]'
                } else if (esDiaSinFichaje) {
                  bgColor = 'bg-[rgba(245,158,11,.06)]'
                  textColor = 'text-[#A86A06]'
                }
                return (
                  <div
                    key={item.fecha}
                    className={`${bgColor} min-h-[70px] p-1 cursor-pointer hover:ring-2 hover:ring-[var(--ink-3)]/30 flex flex-col items-center justify-center transition-all`}
                    onClick={() => {
                      if (f) { setVistaCalendario(false); setFichajeExpandido(f.id) }
                      else if (esDiaSinFichaje) setVistaCalendario(false)
                    }}
                  >
                    <div className={`text-[11px] font-mono tnum ${textColor} mb-0.5`}>{item.dia}</div>
                    {f && f.hora_salida && f.tiempo_efectivo != null && (
                      <div className={`text-[12.5px] font-mono tnum font-medium ${textColor}`}>
                        {formatearDuracion(f.tiempo_efectivo)}
                      </div>
                    )}
                    {f && !f.hora_salida && (<div className="text-[10px] text-[#C13030] font-medium">Sin salida</div>)}
                    {!f && festivoDelDia && (
                      <div className="text-[10px] text-[#E11D48] font-medium truncate max-w-full px-0.5" title={festivoDelDia.nombre}>
                        {festivoDelDia.nombre}
                      </div>
                    )}
                    {!f && !festivoDelDia && ausenciaDelDia && (
                      <div className="text-[10px] text-[#7C3AED] font-medium">
                        {ausenciaDelDia.tipo === 'vacaciones' ? 'Vacaciones' : 'Permiso'}
                      </div>
                    )}
                    {!f && !festivoDelDia && !ausenciaDelDia && esDiaSinFichaje && (
                      <div className="text-[10px] text-[#A86A06] font-medium">Olvidado</div>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="flex flex-wrap gap-3 px-5 py-3 text-[10.5px] text-[var(--ink-3)] border-t border-[var(--line)]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(16,185,129,.10)] rounded-sm" />Completo</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(239,68,68,.08)] rounded-sm" />Sin salida</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(37,99,235,.08)] rounded-sm" />Corregido</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(244,63,94,.08)] rounded-sm" />Festivo</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(124,58,237,.08)] rounded-sm" />Permiso</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-[rgba(245,158,11,.08)] rounded-sm" />Olvidado</span>
            </div>
          </Card>
        )}

        {/* Lista */}
        {!vistaCalendario && (
          <>
            {loading ? (
              <Card eyebrow="Detalle" title="Cargando…">
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="h-14 rounded-soft bg-[var(--bg-2)] animate-pulse" />
                  ))}
                </div>
              </Card>
            ) : todasLasFechas().length === 0 ? (
              <Card>
                <Empty
                  title="Sin fichajes."
                  sub="No se encontraron fichajes para el período seleccionado."
                />
              </Card>
            ) : (
              <div className="space-y-3">
                {todasLasFechas().map((fecha) => {
                  const ausenciaDelDia = getAusenciaParaFecha(fecha)
                  const fichajesDelDia = fichajesPorDia[fecha]

                  if (ausenciaDelDia && !fichajesDelDia) {
                    const tipoLabel = ausenciaDelDia.tipo === 'vacaciones' ? 'VACACIONES'
                      : ausenciaDelDia.tipo === 'baja_medica' ? 'BAJA MÉDICA'
                      : ausenciaDelDia.tipo === 'permiso_retribuido' ? 'PERMISO RETRIBUIDO'
                      : ausenciaDelDia.tipo === 'asuntos_propios' ? 'ASUNTOS PROPIOS'
                      : 'PERMISO'
                    return (
                      <div key={`ausencia-${fecha}`} className="ui-card overflow-hidden">
                        <div className="px-4 py-2.5 bg-[rgba(124,58,237,.04)] border-b border-[rgba(124,58,237,.15)] flex items-center gap-2">
                          <FiCalendar size={13} className="text-[#7C3AED]" />
                          <span className="text-[12.5px] font-medium text-[var(--ink)]">{formatearFecha(fecha)}</span>
                          <Pill tone="purple">{tipoLabel}</Pill>
                        </div>
                        <div className="px-4 py-3">
                          <p className="text-[13px] text-[var(--ink-2)]">{ausenciaDelDia.motivo || 'Ausencia aprobada'}</p>
                          {ausenciaDelDia.fecha_inicio !== ausenciaDelDia.fecha_fin && (
                            <p className="text-[11px] text-[var(--ink-3)] mt-1">
                              Del {formatearFecha(ausenciaDelDia.fecha_inicio)} al {formatearFecha(ausenciaDelDia.fecha_fin)}
                            </p>
                          )}
                        </div>
                      </div>
                    )
                  }

                  const diaSinFichaje = diasSinFichaje.find(d => d.fecha === fecha)
                  if (diaSinFichaje && !fichajesDelDia) {
                    const isSelected = diasOlvidadosSeleccionados.includes(fecha)
                    const haySeleccionGrupal = diasOlvidadosSeleccionados.length > 0
                    return (
                      <div
                        key={`sin-${fecha}`}
                        className={`ui-card overflow-hidden ${isSelected ? 'ring-2 ring-[var(--brand-1)]/40 bg-[rgba(37,99,235,.04)]' : ''}`}
                      >
                        <div className="px-4 py-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            {/* Sin corrección ni ausencias la selección no lleva
                                a ninguna acción disponible. */}
                            {(puedeCorregir || puedeAusencias) && (
                              <button
                                type="button"
                                onClick={() => setDiasOlvidadosSeleccionados(prev =>
                                  prev.includes(fecha) ? prev.filter(f => f !== fecha) : [...prev, fecha]
                                )}
                                className="shrink-0"
                              >
                                {isSelected
                                  ? <FiCheckSquare size={15} className="text-[var(--brand-1)]" />
                                  : <FiSquare size={15} className="text-[var(--ink-3)] hover:text-[var(--ink-2)]" />}
                              </button>
                            )}
                            <FiAlertCircle size={14} className="text-[#A86A06] shrink-0" />
                            <span className="text-[13px] font-medium text-[var(--ink)]">{formatearFecha(fecha)}</span>
                            <Pill tone="amber">OLVIDADO</Pill>
                          </div>
                          {!haySeleccionGrupal && (puedeCorregir || puedeAusencias) && (
                            <div className="flex items-center gap-1">
                              {puedeCorregir && (
                                <button
                                  type="button"
                                  onClick={() => setModalCorreccion({ open: true, fecha })}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[var(--brand-1)] hover:bg-[rgba(37,99,235,.06)] transition-colors"
                                  title="Solicitar corrección"
                                >
                                  <FiEdit size={14} />
                                </button>
                              )}
                              {puedeAusencias && (
                                <button
                                  type="button"
                                  onClick={() => setModalAusencia({ open: true, fecha })}
                                  className="inline-flex items-center justify-center w-8 h-8 rounded-soft text-[var(--ink-3)] hover:text-[#7C3AED] hover:bg-[rgba(124,58,237,.06)] transition-colors"
                                  title="Declarar ausencia"
                                >
                                  <FiCalendar size={14} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  }

                  if (!fichajesDelDia) return null

                  const diaExpandido = diasExpandidos.includes(fecha)
                  const totalFichajes = fichajesDelDia.length
                  const tiempoTotalDia = fichajesDelDia.reduce((acc, f) => acc + (f.tiempo_efectivo || f.tiempo_total || 0), 0)

                  if (totalFichajes === 1) {
                    const fichaje = fichajesDelDia[0]
                    return (
                      <div key={fecha} className="ui-card overflow-hidden">
                        <div className="px-4 py-2 bg-[var(--bg-2)] border-b border-[var(--line)] flex items-center gap-2">
                          <FiCalendar size={13} className="text-[var(--ink-3)]" />
                          <span className="text-[12.5px] font-medium text-[var(--ink)]">{formatearFecha(fecha)}</span>
                        </div>
                        {renderFichajeRow(fichaje)}
                      </div>
                    )
                  }

                  // Múltiples fichajes en el día
                  return (
                    <div key={fecha} className="ui-card overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleDiaExpandido(fecha)}
                        className="w-full px-4 py-3 flex items-center justify-between hover:bg-[var(--bg-2)] transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <FiCalendar size={16} className="text-[var(--ink-3)]" />
                          <div className="text-left">
                            <div className="text-[13px] font-medium text-[var(--ink)]">{formatearFecha(fecha)}</div>
                            <div className="text-[11px] text-[var(--ink-3)]">
                              {totalFichajes} fichajes · {formatearDuracion(tiempoTotalDia)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Pill tone="brand">{totalFichajes}</Pill>
                          {diaExpandido ? <FiChevronUp size={16} className="text-[var(--ink-3)]" /> : <FiChevronDown size={16} className="text-[var(--ink-3)]" />}
                        </div>
                      </button>
                      {diaExpandido && (
                        <div className="border-t border-[var(--line)] divide-y divide-[var(--line)]">
                          {fichajesDelDia.map((fichaje) => (
                            <div key={fichaje.id}>{renderFichajeRow(fichaje)}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Punto C — pantalla unificada de corrección a nivel de marcaje */}
      {modalCorreccion.open && (
        <CorreccionDiaModal
          modo="empleado"
          fechaInicial={modalCorreccion.fecha}
          onClose={() => setModalCorreccion({ open: false, fecha: null })}
          onSuccess={() => {
            setModalCorreccion({ open: false, fecha: null })
            setDiasOlvidadosSeleccionados([])
            cargarFichajes(); cargarMisSolicitudes(); cargarDiasSinFichaje()
          }}
        />
      )}

      {modalAusencia.open && (
        <SolicitudAusenciaModal
          ausencia={null}
          fechaSugerida={modalAusencia.fecha}
          fechaSugeridaFin={modalAusencia.fechaFin || modalAusencia.fecha}
          onClose={() => setModalAusencia({ open: false, fecha: null, fechaFin: null })}
          onSuccess={() => {
            setModalAusencia({ open: false, fecha: null, fechaFin: null })
            setDiasOlvidadosSeleccionados([])
            cargarDiasSinFichaje()
          }}
        />
      )}

      {/* Barra flotante de selección grupal */}
      {diasOlvidadosSeleccionados.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 ui-card px-4 py-3 flex items-center gap-3">
          <span className="text-[13px] font-medium text-[var(--ink-2)]">
            {diasOlvidadosSeleccionados.length} día{diasOlvidadosSeleccionados.length > 1 ? 's' : ''} seleccionado{diasOlvidadosSeleccionados.length > 1 ? 's' : ''}
          </span>
          {puedeCorregir && (
            <Btn
              variant="primary"
              size="sm"
              onClick={() => {
                const fechas = [...diasOlvidadosSeleccionados].sort()
                setModalCorreccion({ open: true, fecha: fechas[0] })
              }}
            >
              <FiEdit size={13} /> Corrección
            </Btn>
          )}
          {puedeAusencias && (
            <Btn
              variant="ghost"
              size="sm"
              onClick={() => {
                const fechas = [...diasOlvidadosSeleccionados].sort()
                setModalAusencia({ open: true, fecha: fechas[0], fechaFin: fechas[fechas.length - 1] })
              }}
            >
              <FiCalendar size={13} /> Ausencia
            </Btn>
          )}
          <button
            type="button"
            onClick={() => setDiasOlvidadosSeleccionados([])}
            className="inline-flex items-center justify-center w-7 h-7 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] transition-colors"
            title="Cancelar selección"
          >
            <FiXCircle size={15} />
          </button>
        </div>
      )}

      {modalHoraExtra && (
        <ModalHoraExtra
          fichajeId={modalHoraExtra.fichaje_id}
          minutosExtra={modalHoraExtra.minutosExtra}
          minutosTurno={modalHoraExtra.minutosTurno}
          onClose={() => setModalHoraExtra(null)}
          onSuccess={() => { setModalHoraExtra(null); cargarMisHorasExtras() }}
        />
      )}
    </>
  )
}

export default HistorialFichajesV2
